import {
  type Conversation,
  type InferenceOptions,
  type Message,
  MessageRole,
} from "@superego/backend";
import { useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import { useContinueConversation } from "../../../business-logic/backend/hooks.js";
import toasts from "../../../business-logic/toasts/toasts.js";
import ToastType from "../../../business-logic/toasts/ToastType.js";
import classnames from "../../../utils/classnames.js";
import last from "../../../utils/last.js";
import ConversationMessages from "../ConversationMessages/ConversationMessages.js";
import UserMessageContentInput from "../UserMessageContentInput/UserMessageContentInput.js";
import * as cs from "./Chat.css.js";

interface Props {
  conversation: Conversation;
  userMessageContentInputPlaceholder: string;
  showToolsCalls: boolean;
  className?: string | undefined;
}
export default function Chat({
  conversation,
  userMessageContentInputPlaceholder,
  showToolsCalls,
  className,
}: Props) {
  const intl = useIntl();
  const { isPending, mutate } = useContinueConversation();

  // When messages change, scroll to bottom and - if the last message is an
  // assistant message - focus the input.
  const lastMessage = last(conversation.messages);
  const lastMessageId =
    lastMessage && "id" in lastMessage ? lastMessage.id : null;
  const lastMessageCreatedAtTime =
    lastMessage && "createdAt" in lastMessage
      ? lastMessage.createdAt.getTime()
      : null;
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (lastMessageId) {
      // Hack: scrolling doesn't work unless we delay it a bit.
      setTimeout(() => {
        document.querySelector('[data-slot="Main"]')?.scrollTo({
          top: 1e6,
          behavior:
            lastMessageCreatedAtTime &&
            Date.now() - lastMessageCreatedAtTime < 10_000
              ? "smooth"
              : "instant",
        });
      }, 15);
    }
    if (lastMessage?.role === MessageRole.Assistant) {
      inputRef.current?.focus();
    }
    // We want to avoid scrolling if the last message didn't change, even if the
    // ref of the message object changed. Since messages are immutable, we can use
    // the id as a stable identity: id didn't change => message didn't change.
    // oxlint-disable-next-line react/exhaustive-deps
  }, [lastMessageId]);

  const onSend = async (
    messageContent: Message.User["content"],
    inferenceOptions: InferenceOptions<"completion">,
  ) => {
    const { error } = await mutate(
      conversation.id,
      messageContent,
      inferenceOptions,
    );
    if (error) {
      console.error(error);
      toasts.add({
        type: ToastType.Error,
        title: intl.formatMessage({
          defaultMessage: "Error sending message to assistant",
        }),
        error: error,
      });
    }
  };

  return (
    <div className={classnames(cs.Chat.root, className)}>
      <div className={cs.Chat.userMessageContentInputContainer}>
        <UserMessageContentInput
          conversation={conversation}
          onSend={onSend}
          isSending={isPending}
          placeholder={userMessageContentInputPlaceholder}
          autoFocus={true}
          textAreaRef={inputRef}
        />
      </div>
      <ConversationMessages
        conversation={conversation}
        className={cs.Chat.messages}
        showToolCalls={showToolsCalls}
      />
    </div>
  );
}
